import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Service, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AUTH_REFRESH_TOKEN_CACHE_KEY, AUTH_TOKEN_CACHE_KEY } from '@core/constants/auth.constants';
import { APP_PERMISSIONS, APP_ROLES } from '@core/constants/permission.constants';
import { APP_ROUTES } from '@core/constants/routes.constants';
import { CR_WEB_API, crWebApiUrl } from '@core/constants/cr-web-api.constants';
import { USERS_API, usersApiUrl } from '@core/constants/users-api.constants';
import { CacheService } from '@core/services/cache.service';
import { UIService } from '@core/services/ui.service';
import { jwtDecode as decode } from 'jwt-decode';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { IUserProfileDto } from './user-profile.type';
import {
  IAuthInfo,
  IChangePasswordRequest,
  IPermissionDto,
  IToken,
  IUserAccountInfoV2,
  IUsersLoginRequest,
  IUsersRefreshRequest,
  IUsersRegisterRequest,
  IUsersResetPasswordRequest,
  IUsersSetPasswordRequest,
} from './types';

@Service()
export class AuthService {
  private readonly isAuthenticated = signal<boolean>(false);
  private readonly currentUserSignal = signal<IUserProfileDto | null>(null);
  private readonly userPermissionsSignal = signal<string[]>([]);
  private readonly accountInfoSignal = signal<IUserAccountInfoV2 | null>(null);
  /** Shown in the nav user menu when profile is not loaded (email from API or JWT). */
  private readonly userMenuLabelSignal = signal<string | null>(null);

  readonly currentUser = computed(() => this.currentUserSignal());
  readonly userPermissions = computed(() => this.userPermissionsSignal());
  readonly userRoles = computed(() => this.accountInfoSignal()?.roles ?? []);
  readonly accountInfo = computed(() => this.accountInfoSignal());
  readonly userMenuLabel = computed(() => this.userMenuLabelSignal());

  private readonly httpClient = inject(HttpClient);
  private readonly cacheService = inject(CacheService);
  private readonly router = inject(Router);
  private readonly uiService = inject(UIService);
  private refreshInProgress$: Observable<IToken> | null = null;
  private userAccessRequest$: Observable<void> | null = null;
  private readonly userAccessLoaded = signal<boolean>(false);

  authenticate(accessToken?: string): Observable<boolean> {
    const token = accessToken ?? this.getAccessToken();
    if (!token) {
      return of(false);
    }
    if (!this.hasTokenExpired(token)) {
      if (!this.isAuthenticated()) {
        this.isAuthenticated.set(true);
        this.getUserInfoAndPermissions();
      }
      return of(true);
    }
    if (this.isAuthenticated()) {
      return of(true);
    }
    return this.refreshToken().pipe(
      switchMap(() => {
        this.isAuthenticated.set(true);
        this.getUserInfoAndPermissions();
        return of(true);
      }),
      catchError(() => of(false))
    );
  }

  login(email: string, password: string) {
    const body: IUsersLoginRequest = {
      email,
      password,
      twoFactorCode: null,
      twoFactorRecoveryCode: null,
    };
    return this.httpClient.post<IToken>(usersApiUrl(USERS_API.LOGIN), body).pipe(
      tap((value) => {
        this.setTokens(value.accessToken, value.refreshToken);
      }),
      switchMap((value) => this.authenticate(value.accessToken))
    );
  }

  register(payload: IUsersRegisterRequest) {
    return this.httpClient.post<void>(usersApiUrl(USERS_API.REGISTER), payload);
  }

  requestPasswordReset(email: string) {
    return this.httpClient.post<void>(usersApiUrl(USERS_API.FORGOT_PASSWORD), { email });
  }

  setPassword(payload: IUsersSetPasswordRequest) {
    return this.httpClient.post<void>(usersApiUrl(USERS_API.SET_PASSWORD), payload);
  }

  resetPassword(payload: IUsersResetPasswordRequest) {
    return this.httpClient.post<void>(usersApiUrl(USERS_API.RESET_PASSWORD), payload);
  }

  logout() {
    this.clearToken();
    this.isAuthenticated.set(false);
    this.userPermissionsSignal.set([]);
    this.currentUserSignal.set(null);
    this.accountInfoSignal.set(null);
    this.userMenuLabelSignal.set(null);
    this.invalidateUserAccess();
    this.router.navigate([APP_ROUTES.AUTH, APP_ROUTES.LOGIN]);
  }

  getAccessToken(): string {
    return this.cacheService.getItem<string>(AUTH_TOKEN_CACHE_KEY) ?? '';
  }

  getRefreshToken(): string {
    return this.cacheService.getItem<string>(AUTH_REFRESH_TOKEN_CACHE_KEY) ?? '';
  }

  hasPermission(permission: string): boolean {
    if (!permission) {
      return true;
    }
    if (permission === APP_PERMISSIONS.UsersRead) {
      return true;
    }
    const perms = this.userPermissions();
    if (perms.includes(permission)) {
      return true;
    }
    const roles = this.accountInfo()?.roles ?? [];
    if (roles.some((r) => r === APP_ROLES.Administrator)) {
      if (this.userAccessLoaded()) {
        return perms.includes(permission);
      }
      return true;
    }
    return false;
  }

  changePassword({
    currentPassword,
    newPassword,
    confirmPassword: _confirmPassword,
  }: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) {
    const body: IChangePasswordRequest = { currentPassword, newPassword };
    return this.httpClient.post<void>(usersApiUrl(USERS_API.CHANGE_PASSWORD), body);
  }

  public refreshUserInfo() {
    this.invalidateUserAccess();
    this.getUserInfoAndPermissions();
  }

  /**
   * Resolves account info and effective permissions for guards.
   * Loaded once per session; reuses cached signals on later navigations.
   */
  ensureUserAccessLoaded$(): Observable<void> {
    if (this.userAccessLoaded()) {
      return of(void 0);
    }
    return this.getOrCreateUserAccessLoad$();
  }

  public refreshToken(): Observable<IToken> {
    this.uiService.setIsLoading(true);
    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }
    const body: IUsersRefreshRequest = {
      refreshToken: this.getRefreshToken(),
    };
    this.refreshInProgress$ = this.httpClient
      .post<IToken>(usersApiUrl(USERS_API.REFRESH), body)
      .pipe(
        tap((value) => this.setTokens(value.accessToken, value.refreshToken)),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.logout();
          }
          return throwError(() => error);
        }),
        shareReplay(1),
        finalize(() => {
          this.uiService.setIsLoading(false);
          this.refreshInProgress$ = null;
        })
      );
    return this.refreshInProgress$;
  }

  private getUserInfoAndPermissions() {
    this.getOrCreateUserAccessLoad$().subscribe();
  }

  private invalidateUserAccess() {
    this.userAccessLoaded.set(false);
    this.userAccessRequest$ = null;
  }

  private getOrCreateUserAccessLoad$(): Observable<void> {
    if (!this.userAccessRequest$) {
      this.userAccessRequest$ = this.buildUserAccess$().pipe(
        tap(() => this.userAccessLoaded.set(true)),
        shareReplay({ bufferSize: 1, refCount: false }),
        finalize(() => {
          this.userAccessRequest$ = null;
        })
      );
    }
    return this.userAccessRequest$;
  }

  private buildUserAccess$(): Observable<void> {
    return forkJoin({
      profile: this.httpClient
        .get<IUserProfileDto>(usersApiUrl(USERS_API.ME))
        .pipe(catchError(() => of(null))),
      account: this.httpClient
        .get<IUserAccountInfoV2>(usersApiUrl(USERS_API.MANAGE_INFO_V2))
        .pipe(catchError(() => of(null))),
    }).pipe(
      switchMap(({ profile, account }) => {
        const roles = account?.roles ?? [];
        if (roles.length === 0) {
          return of({ profile, account, permissions: [] as IPermissionDto[] });
        }
        const rolePermRequests = roles.map((roleName) =>
          this.httpClient
            .get<IPermissionDto[]>(crWebApiUrl(CR_WEB_API.ROLE_PERMISSIONS(roleName)))
            .pipe(catchError(() => of([] as IPermissionDto[])))
        );
        return forkJoin(rolePermRequests).pipe(
          map((permsArrays) => ({
            profile,
            account,
            permissions: permsArrays.flat(),
          }))
        );
      }),
      tap(({ profile, account, permissions }) => {
        this.currentUserSignal.set(profile);
        this.accountInfoSignal.set(account);
        this.userMenuLabelSignal.set(account?.email?.trim() || this.tryEmailFromAccessToken());
        const names = new Set<string>();
        for (const p of permissions) {
          if (p?.name) {
            names.add(p.name);
          }
        }
        this.userPermissionsSignal.set([...names]);
      }),
      map(() => void 0)
    );
  }

  private hasTokenExpired(token: string): boolean {
    try {
      const currentUser = this.getAuthInfoFromToken(token);
      return currentUser.exp < Date.now() / 1000;
    } catch {
      return true;
    }
  }

  private setTokens(accessToken: string, refreshToken: string) {
    this.cacheService.setItem(AUTH_TOKEN_CACHE_KEY, accessToken);
    this.cacheService.setItem(AUTH_REFRESH_TOKEN_CACHE_KEY, refreshToken);
  }

  private getAuthInfoFromToken(token: string): IAuthInfo {
    return decode(token);
  }

  private tryEmailFromAccessToken(): string | null {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }
    try {
      const info = this.getAuthInfoFromToken(token);
      return info.email?.trim() || null;
    } catch {
      return null;
    }
  }

  private clearToken() {
    this.cacheService.removeItem(AUTH_TOKEN_CACHE_KEY);
    this.cacheService.removeItem(AUTH_REFRESH_TOKEN_CACHE_KEY);
  }
}
